/** @jsx jsx */
import { jsx } from '@emotion/core';
import { RouteComponentProps } from '@reach/router';
import { navigate } from 'gatsby';
import React, { Fragment, useEffect, useState } from 'react';
import useContentMgmt from '../../../../hooks/useContentMgmt';
import {
  Box,
  Button,
  createStyles,
  Divider,
  FormHelperText,
  Grid,
  Hidden,
  makeStyles,
  MenuItem,
  Select,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';
import { ExpandMore, ExpandLess } from '@material-ui/icons';

import ROUTES from '../../../../routes';
import { selectAddressForm } from '../forms';
import microcopyGroupIds from '../../../pge-plus-common/microcopyGroupIds';
import { useTranslation } from '../../../../hooks/useTranslation';
import {
  Maybe,
  SearchServiceAddress,
} from '../../../../__generated__/pge-types';
import ServiceAddressLookup from '../../../service-address-lookup/ServiceAddressLookup';
import colors from '../../../../themes/main-colors';
import {
  DEFAULT_MAP_CENTER,
  isLocationWithinPGEServiceBoundary,
} from '../../../../util/map-utils';
import { useIsMobile } from '../../../../util/style-utils';
import { usePremiseSearchQuery } from '../../../../hooks/usePremiseSearchQuery';
import Backdrop from '../../../backdrop';
import { AddressSuggestion } from '../../../ssm/common/AddressSuggestion/AddressSuggestion';
import StaticMap from '../../../static-map';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    rootContainer: () => ({
      '& .mapContainerRoot': {
        minHeight: '300px',
      },
    }),
    header: {
      fontWeight: 'bold',
      fontSize: '20px',
    },
    marginBottom: {
      marginBottom: '-15px',
    },
    nextBtn: {
      fontSize: '18px',
      padding: '5px 15px',
      flexBasis: '25%',
      borderRadius: '30px',

      backgroundColor: colors.buttonHoverPrimary,
      border: `1px solid ${colors.buttonHoverPrimary}`,
      color: colors.white,
      '&:hover': {
        backgroundColor: colors.buttonHoverPrimary,
        borderColor: colors.buttonHoverPrimary,
        color: colors.white,
      },
    },
    cancelBtn: {
      fontSize: '18px',
      padding: '5px 15px',
      flexBasis: '25%',
      borderRadius: '30px',
      color: colors.buttonHoverPrimary,
      backgroundColor: colors.white,
      border: `2px solid ${colors.sparkBlue}`,
      '&:hover': {
        backgroundColor: colors.white,
        border: `2px solid ${colors.sparkBlue}`,
      },
    },
    container: {
      maxWidth: 790,
      margin: '35px auto 135px auto',
      [`@media (max-width: ${theme.breakpoints.values.sm}px)`]: {
        maxWidth: '100%',
      },
    },
    box: {
      display: 'flex',
      width: '100%',
      justifyContent: 'center',
      margin: '16px 0 0 auto',
      gap: '16px',
    },
    text: {
      fontFamily: 'Untitled-Sans',
      whiteSpace: 'nowrap',
      marginRight: '8px',
      marginBottom: '16px',
    },
    select: {
      backgroundColor: colors.white,
      minHeight: '55px',
      minWidth: '100%',
    },
    divider: {
      backgroundColor: colors.white,
      minHeight: '55px',
      minWidth: '100%',
    },
  }),
);

export const SelectAddress: React.FC<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const { content } = useContentMgmt(microcopyGroupIds.PGE_PLUS_CONSULTATION);
  const { form } = selectAddressForm(content);
  const theme = useTheme();
  const isMobile = useIsMobile();
  const {
    premiseIds,
    setPremiseIds,
    loading: premiseSearchLoading,
    error,
    data: premiseData,
  } = usePremiseSearchQuery();

  const { t, richT } = useTranslation();
  const classes = useStyles();

  const ASSOCIATED_TO_HOME_OPTIONS = [
    content.get('I_LIVE_AT_THIS_ADDRESS'),
    content.get('I_OWE_HOME'),
    content.get('REQUESTING_ON_SOMEONE_BEHALF'),
  ];

  const [currentPosition, setCurrentPosition] = useState<
    { lat: number; lng: number } | undefined
  >();
  const [isLoaded, setLoaded] = useState(false);
  const [isAddressHelpDisplayed, setIsAddressHelpDisplayed] = useState<boolean>(
    false,
  );
  const [
    isAddressSuggestionModalDisplayed,
    setIsAddressSuggestionModalDisplayed,
  ] = useState<boolean>(false);
  const [addressSuggestions, setAddressSuggestions] = useState<
    Maybe<SearchServiceAddress>[]
  >([]);
  const [serviceAddressQueryLoader, setServiceAddressQueryLoader] = useState<
    boolean
  >(false);
  const [isAddressVerified, setIsAddressVerified] = useState<boolean>(false);
  const [loadingAddressSuggestions, setLoadingAddressSuggestions] = useState<
    boolean
  >(false);

  useEffect(() => {
    void form.setValue('associationWithAddress', ASSOCIATED_TO_HOME_OPTIONS[0]);
  }, [content]);

  useEffect(() => {
    if (isLoaded) {
      navigator.geolocation.getCurrentPosition(position => {
        const { latitude, longitude } = position.coords;
        if (
          isLocationWithinPGEServiceBoundary({ lat: latitude, lng: longitude })
        ) {
          setCurrentPosition({
            lat: latitude,
            lng: longitude,
          });
        }
      });
    }
  }, [isLoaded]);

  useEffect(() => {
    if (premiseData?.premiseSearch?.length) {
      const firstPremiseGeoLocation = premiseData.premiseSearch[0].spList![0]
        ?.geolocation;
      if (firstPremiseGeoLocation) {
        const [latitude, longitude] = firstPremiseGeoLocation.split(',');
        setCurrentPosition({
          lat: Number(latitude),
          lng: Number(longitude),
        });
      }
    }
  }, [premiseData]);

  const handleAddressSelected = (address: SearchServiceAddress) => {
    let completeAddress: string = '';
    if (address && address.addressLine1 && address.city && address.postal) {
      completeAddress = `${address.addressLine1.trim()}, ${address.city.trim()}, ${address.postal.trim()}`;
    }
    if (completeAddress) {
      setIsAddressVerified(true);
    } else {
      setIsAddressVerified(false);
    }
    setIsAddressSuggestionModalDisplayed(false);
    setAddressSuggestions([]);
    setPremiseIds(address.premiseIds!);
    void form.setValue('address', completeAddress);
    void form.setValue('inputAddress', completeAddress);
  };

  const handleClickCancel = () => {
    void navigate(ROUTES.HOME);
  };

  const handleClickProceed = () => {
    if (!isAddressVerified) {
      setIsAddressSuggestionModalDisplayed(true);
    } else {
      void navigate(ROUTES.PGE_PLUS_CONSULTATION_DETAILS, {
        state: {
          address: form.values.address,
          associated: form.values.associationWithAddress,
          encryptedPremiseId: premiseIds![0],
        },
      });
    }
  };

  return (
    <Fragment>
      {premiseSearchLoading ? (
        <Backdrop forceOpen message={t('LOADING')} />
      ) : null}
      <form onSubmit={form.submit(values => handleClickProceed())}>
        <Grid
          container
          direction="column"
          spacing={4}
          xs={12}
          md={9}
          className={classes.container}
        >
          <Grid item container spacing={2}>
            <Grid container item>
              <Typography variant="h1">
                {t('BOOK_A_CONSULTATION_WITH_PGE_SPECIALIST')}
              </Typography>
              <Typography>{t('ANSWER_A_FEW_QUICK_QUESTIONS')}</Typography>
            </Grid>
          </Grid>
          <Grid item container spacing={2}>
            <Grid container item>
              <Hidden smDown>
                <Typography className={classes.header}>
                  {t('WHERE_DO_YOU_NEED_PGE_PLUS_SERVICE')}
                </Typography>
              </Hidden>
              <Typography>{t('FOR_THE_BEST_MATCH_PICK_AN_OPTION')}</Typography>
              <Box>
                <a
                  href="#"
                  onClick={(e: any) => {
                    e.preventDefault();
                    setIsAddressHelpDisplayed(!isAddressHelpDisplayed);
                  }}
                  style={{
                    cursor: 'pointer',
                    textDecoration: 'none',
                    display: 'inline',
                  }}
                >
                  <Box display="flex">
                    <span
                      style={{
                        color: colors.sparkBlue,
                        fontSize: '1rem',
                      }}
                    >
                      {t('MORE_ADDRESS_HELP')}
                    </span>
                    {isAddressHelpDisplayed ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                </a>
                {isAddressHelpDisplayed && (
                  <ul style={{ margin: '0px' }}>
                    <li>
                      {richT(
                        'CHECK_TO_SEE_IF_THE_ADDRESS',
                        undefined,
                        '_blank',
                      )}
                    </li>
                    <li>
                      {richT('CALL_US_AT', {
                        CUSTOMER_SERVICE_NUMBER: t(
                          'CALL_FOR_ASSISTANCE_NUMBER',
                        ),
                      })}
                    </li>
                  </ul>
                )}
              </Box>
            </Grid>
            <Grid container item spacing={0} direction={'column'}>
              <Grid item>
                <ServiceAddressLookup
                  setLoading={setLoadingAddressSuggestions}
                  onAddressChange={handleAddressSelected}
                  setServiceAddressQueryLoader={setServiceAddressQueryLoader}
                  setAddressSuggestions={setAddressSuggestions}
                  inputFieldProps={{
                    variant: 'outlined',
                    label: t('STREET_ADDRESS'),
                    error: false,
                    value: form.values.inputAddress,
                    onChange(e) {
                      void form.setValue('inputAddress', e.target.value);
                    },
                  }}
                  autocompleteProps={{
                    freeSolo: false,
                    inputValue: form.values.inputAddress,
                  }}
                />
              </Grid>
              {form.errors.inputAddress && (
                <FormHelperText error={true} css={{ marginBottom: '8px' }}>
                  {form.errors.inputAddress}
                </FormHelperText>
              )}
              <Grid item className={classes.rootContainer}>
                <StaticMap
                  center={currentPosition ?? DEFAULT_MAP_CENTER}
                  marker={currentPosition}
                  onLoad={() => setLoaded(true)}
                />
              </Grid>
            </Grid>
          </Grid>
          {form.values.address && (
            <Grid item container spacing={2}>
              <Typography
                css={{
                  fontFamily: 'Untitled-Sans',
                  marginRight: '8px',
                }}
              >
                <b>{content.get('ADDRESS_SELECTED_FOR_CONSULTATION')}</b>
                {isMobile ? <br /> : ' '}
                {form.values.inputAddress}
              </Typography>
            </Grid>
          )}
          <Grid item container spacing={2}>
            <Box css={{ marginTop: '16px', width: '100%' }}>
              <Typography className={classes.text}>
                {content.get('HOW_ARE_YOU_ASSOCIATED_WITH_THIS_ADDRESS')}
              </Typography>
              <Select
                className={classes.select}
                variant="outlined"
                displayEmpty={true}
                renderValue={() => form.values.associationWithAddress}
                style={{
                  width: '100%',
                  height: '48px',
                  overflow: 'hidden',
                  fontSize: '16px',
                }}
                inputProps={{ maxLength: 90, fontSize: 16 }}
                onChange={event =>
                  form.setValue(
                    'associationWithAddress',
                    event.target.value as string,
                  )
                }
              >
                {ASSOCIATED_TO_HOME_OPTIONS.map((option, idx) => {
                  return (
                    <MenuItem key={idx} value={option}>
                      {option}
                    </MenuItem>
                  );
                })}
              </Select>
            </Box>
          </Grid>
          <Divider css={{ margin: '16px 0' }} />
          <Grid item container spacing={2}>
            <Box className={classes.box}>
              <Button
                variant="outlined"
                color="primary"
                className={classes.cancelBtn}
                onClick={handleClickCancel}
              >
                {content.get('REUSABLE_CANCEL')}
              </Button>
              <Button
                type={'submit'}
                disabled={loadingAddressSuggestions}
                className={classes.nextBtn}
              >
                {content.get('REUSABLE_BUTTON_NEXT')}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
      <AddressSuggestion
        isOpen={isAddressSuggestionModalDisplayed}
        handleOnModalCancelled={() =>
          setIsAddressSuggestionModalDisplayed(false)
        }
        userAddress={form.values.inputAddress || ''}
        loading={serviceAddressQueryLoader}
        addressSuggestions={addressSuggestions}
        handleSuggestedAddressSubmit={handleAddressSelected}
      />
    </Fragment>
  );
};
